import { useState, useCallback, useMemo } from "react";
import type { FilingType } from "@taxassist/api-client";
import type { UserFriendlyStatus } from "../utils/statusMapping";

export interface JobFilters {
  search?: string;
  userFriendlyStatuses?: UserFriendlyStatus[];
  filingTypes?: FilingType[];
  dateRange?: {
    start: Date | null;
    end: Date | null;
  };
}

interface UseJobFiltersReturn {
  filters: JobFilters;
  searchQuery: string;
  activeFiltersCount: number;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: JobFilters) => void;
  updateFilters: (partialFilters: Partial<JobFilters>) => void;
  clearFilter: (filterKey: keyof JobFilters) => void;
  clearAllFilters: () => void;
}

/**
 * Hook for managing job filters state
 */
export function useJobFilters(
  initialFilters: JobFilters = {},
): UseJobFiltersReturn {
  const [filters, setFiltersState] = useState<JobFilters>(initialFilters);
  const [searchQuery, setSearchQueryState] = useState(
    initialFilters.search || "",
  );

  // Sync search query with filters
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
    setFiltersState((prev) => ({ ...prev, search: query }));
  }, []);

  const setFilters = useCallback((newFilters: JobFilters) => {
    setFiltersState(newFilters);
    setSearchQueryState(newFilters.search || "");
  }, []);

  const updateFilters = useCallback((partialFilters: Partial<JobFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...partialFilters }));
    if (partialFilters.search !== undefined) {
      setSearchQueryState(partialFilters.search);
    }
  }, []);

  const clearFilter = useCallback((filterKey: keyof JobFilters) => {
    setFiltersState((prev) => ({
      ...prev,
      [filterKey]:
        filterKey === "dateRange" ? { start: null, end: null } : undefined,
    }));

    if (filterKey === "search") {
      setSearchQueryState("");
    }
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState({});
    setSearchQueryState("");
  }, []);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.userFriendlyStatuses?.length) count++;
    if (filters.filingTypes?.length) count++;
    if (filters.dateRange?.start || filters.dateRange?.end) count++;
    return count;
  }, [filters]);

  return {
    filters,
    searchQuery,
    activeFiltersCount,
    setSearchQuery,
    setFilters,
    updateFilters,
    clearFilter,
    clearAllFilters,
  };
}
